import React, { useState, useEffect, useCallback } from "react";
import "./UserCalendarScreen.css";
import ScreenHeader from "./ReuseComponent/ScreenHeader";
import { faCalendarDays } from "@fortawesome/free-solid-svg-icons";
import { useLocation, Link } from "react-router-dom";
import Calendar from "react-calendar";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { API_IP, STRIPE_KEY } from "../global_variables";
import { axiosReqConfig } from "../functions/axiosRequest";
import Modal from "./UserCalendarScreen/CalendarMainModal";
import RescheduleModal from "./UserCalendarScreen/RescheduleModal";
import PromptModal from "./ReuseComponent/PromptModal";
import PaymentModal from "./AccountComponents/PaymentModal";
import EtransferModal from "./AccountComponents/EtransferModal";
import MethodChoiceModal from "./AccountComponents/MethodChoiceModal";
import {
  FiCalendar,
  FiClock,
  FiUser,
  FiBriefcase,
  FiRefreshCw,
  FiCreditCard,
  FiEye,
  FiCheck,
  FiPlus,
} from "react-icons/fi";

const stripePromise = loadStripe(STRIPE_KEY);

/* "Needs payment" mirrors BookingScreen step 2:
   provider accepted (booking_acceptance === true + date_acceptance present)
   AND booking is not yet paid AND service not yet completed. */
const needsPayment = (ev) =>
  ev &&
  ev.booking_acceptance === true &&
  !!ev.date_acceptance &&
  ev.paid !== true &&
  ev.completed !== true;

const UserCalendarScreen = ({ setSelectedSection }) => {
  const username = localStorage.getItem("username");
  const [isRefresh, setIsRefresh] = useState(false);
  const [isCalendarVisible, setIsCalendarVisible] = useState(false);
  const [currentWeekStart, setCurrentWeekStart] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [eventDetail, setEventDetail] = useState([]);
  const [eventStatusStyle, setEventStatusStyle] = useState("");
  const [eventStatus, setEventStatus] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [patientData, setPatientData] = useState([]);
  const [previousEvent, setPreviousEvent] = useState(null);
  const [isPromptVisible, setIsPromptVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAlertVisible, setIsAlertVisible] = useState(false);
  const [alertContent, setAlertContent] = useState("");

  /* Payment flow state — mirrors BookingScreen */
  const [methodChoiceOpen, setMethodChoiceOpen] = useState(false);
  const [paymentVisible, setPaymentVisible] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [clientSecret, setClientSecret] = useState("");
  const [customerSession, setCustomerSession] = useState("");
  const [totalPrice, setTotalPrice] = useState("");
  const [selectedBooking, setSelectedBooking] = useState({});
  const [isPaying, setIsPaying] = useState(false);

  /* Reschedule flow */
  const [rescheduleOpen, setRescheduleOpen] = useState(false);

  const togglePayment = () => setPaymentVisible((v) => !v);

  const requestPayment = () => {
    if (!eventDetail || eventDetail.length === 0) return;
    setSelectedBooking({
      booking_id: eventDetail.fk_booking_id,
      care_service_id: eventDetail.care_service_id,
      provider_id: eventDetail.provider_id,
      provider_name: eventDetail.provider_name,
      care_service_name: eventDetail.care_service_name,
    });
    setMethodChoiceOpen(true);
  };

  const handleMethodChoose = async (method) => {
    setMethodChoiceOpen(false);
    if (method === "etransfer") {
      setPaymentMethod("etransfer");
      setPaymentVisible(true);
      return;
    }
    setIsPaying(true);
    try {
      const res = await axiosReqConfig(`${API_IP}/api/payment`, "post", {
        booking_id: selectedBooking.booking_id,
        service_id: selectedBooking.care_service_id,
        provider_id: selectedBooking.provider_id,
      });
      if (!res?.ok) {
        setIsPaying(false);
        return;
      }
      const { clientSecret, totalPrice, transactionId, customerSession } =
        res.data;
      setSelectedBooking((prev) => ({ ...prev, transactionId }));
      setTotalPrice(totalPrice);
      setCustomerSession(customerSession);
      setClientSecret(clientSecret);
      setPaymentMethod("card");
      setPaymentVisible(true);
    } catch (e) {
      console.error(e);
    } finally {
      setIsPaying(false);
    }
  };

  const goToBookings = () => {
    if (typeof setSelectedSection === "function") {
      setSelectedSection("Booking");
    }
  };

  const openModal = () => setShowModal(true);
  const closeModal = () => setShowModal(false);

  const getEventForCalendars = useCallback(async () => {
    const res = await axiosReqConfig(`${API_IP}/api/calendars`, "get", []);
    const data = res?.ok ? res.data : [];
    setEvents(Array.isArray(data) ? data : []);
  }, [isRefresh]);

  const cancelAppointment = async () => {
    try {
      setIsLoading(true);
      await axiosReqConfig(
        `${API_IP}/api/calendars/${eventDetail.fk_booking_id}`,
        "delete",
        { date: eventDetail.date_day },
      );
      setEventDetail([]);
      toggleIsRefresh();
      setTimeout(() => {
        setIsLoading(false);
        togglePrompt();
      }, 2000);
    } catch (error) {
      alert(error);
    }
  };

  const toggleAlert = () => {
    setIsAlertVisible(!isAlertVisible);
  };

  const togglePrompt = () => {
    if (isPromptVisible === false && eventStatus === "Accepted") {
      setAlertContent(
        "You can only cancel an appointment that has not been accepted or still pending by any provider!",
      );
      setIsAlertVisible(true);
    } else if (eventDetail.length === 0) {
      setAlertContent("Please select a pending appointment");
      setIsAlertVisible(true);
    } else {
      setIsPromptVisible(!isPromptVisible);
    }
  };

  const toggleIsRefresh = () => {
    setIsRefresh(!isRefresh);
  };

  const selectEvent = (selectedEvent, index, event_style, event_status) => {
    if (previousEvent !== null) {
      previousEvent.selected = false;
    }
    setPreviousEvent(selectedEvent);
    selectedEvent.selected = true;
    setEventDetail(selectedEvent);
    setEventStatusStyle(event_style);
    setEventStatus(event_status);
  };

  const timeList = [
    "7:00",
    "8:00",
    "9:00",
    "10:00",
    "11:00",
    "12:00",
    "13:00",
    "14:00",
    "15:00",
    "16:00",
    "17:00",
    "18:00",
    "19:00",
    "20:00",
    "21:00",
    "22:00",
  ];

  const formatDate = (date) => {
    const options = { month: "long", day: "numeric", year: "numeric" };
    return new Intl.DateTimeFormat("en-US", options).format(date);
  };

  const getStartOfWeek = (date) => {
    const startOfWeek = new Date(date);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(startOfWeek.setDate(diff));
  };

  const changeWeek = (days) => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(currentWeekStart.getDate() + days);
    setCurrentWeekStart(getStartOfWeek(newDate));
  };

  const onDateSelect = (date) => {
    setCurrentWeekStart(getStartOfWeek(date));
    setIsCalendarVisible(false);
  };

  const weekStart = getStartOfWeek(currentWeekStart);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);

  const daysOfWeek = [];
  for (let i = 0; i < 7; i++) {
    const currentDay = new Date(weekStart);
    currentDay.setDate(weekStart.getDate() + i);
    daysOfWeek.push(currentDay);
  }

  // FIXED: Logic tính toán vị trí Event với độ chính xác theo phút
  const getEventPosition = (startTime, endTime) => {
    if (!startTime || !endTime) return { top: "0px", height: "0px" };

    const parseTime = (timeStr) => {
      const [hourStr, minStr] = timeStr.split(":");
      return parseInt(hourStr, 10) + parseInt(minStr || 0, 10) / 60;
    };

    const start = parseTime(startTime);
    const end = parseTime(endTime);
    const duration = end - start;

    const rowHeight = 110;
    const headerHeight = 72;

    // Bắt đầu từ mốc 7:00
    const startPosition = (start - 7) * rowHeight + headerHeight;

    return {
      top: `${startPosition}px`,
      height: `${duration * rowHeight}px`,
    };
  };

  function stripTime(date_day) {
    if (typeof date_day === "string") {
      const date = new Date(date_day);
      return new Date(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
      );
    }
    const date = new Date(date_day);
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  const getPatientData = async () => {
    const res = await axiosReqConfig(`${API_IP}/api/patients`, "get", []);
    const data = res?.ok ? res.data : [];
    setPatientData(data || []);
  };

  useEffect(() => {
    let isMounted = true;
    if (isMounted) {
      getEventForCalendars();
      getPatientData();
    }
    return () => {
      isMounted = false;
    };
  }, [getEventForCalendars]);

  return (
    <>
      <Modal
        showModal={showModal}
        handleClose={closeModal}
        patientData={patientData}
        toggleRefresh={toggleIsRefresh}
        setSelectedSection={setSelectedSection}
      />
      <PromptModal
        title="Do you want to cancel this appointment?"
        type="binary"
        showModal={isPromptVisible}
        handleClose={togglePrompt}
        handleConsent={cancelAppointment}
        isLoading={isLoading}
      />
      <PromptModal
        title={alertContent}
        type="alert"
        showModal={isAlertVisible}
        handleClose={toggleAlert}
        isLoading={isLoading}
      />

      {isPaying && !paymentVisible && (
        <div className="UCS-loading-overlay" role="status" aria-live="polite">
          <div className="UCS-loading-modal">
            <img
              src={require("../images/loading_icon.gif")}
              alt="Loading"
            />
            <p>Preparing secure payment…</p>
          </div>
        </div>
      )}

      <MethodChoiceModal
        showModal={methodChoiceOpen}
        handleClose={() => setMethodChoiceOpen(false)}
        bookingId={selectedBooking?.booking_id}
        onChoose={handleMethodChoose}
      />

      <RescheduleModal
        open={rescheduleOpen}
        onClose={() => setRescheduleOpen(false)}
        eventDetail={eventDetail}
        onSuccess={() => {
          setAlertContent("Reschedule request submitted.");
          setIsAlertVisible(true);
          toggleIsRefresh();
        }}
      />

      {paymentVisible && paymentMethod === "card" && clientSecret && (
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret,
            customerSessionClientSecret: customerSession,
          }}
        >
          <PaymentModal
            showModal={paymentVisible}
            handleClose={togglePayment}
            clientSecret={clientSecret}
            customerSession={customerSession}
            totalPrice={totalPrice}
            selectedBooking={selectedBooking}
            toggleRefresh={toggleIsRefresh}
          />
        </Elements>
      )}

      {paymentVisible && paymentMethod === "etransfer" && (
        <EtransferModal
          showModal={paymentVisible}
          handleClose={togglePayment}
          selectedBooking={selectedBooking}
          toggleRefresh={toggleIsRefresh}
        />
      )}
      <div className="screen-container">
        <ScreenHeader
          icon={faCalendarDays}
          title="Calendar"
          subtitle="Manage your schedule and upcoming appointments."
        />

        <div className="UCS-main-wrapper">
          <div className="UCS-calendar-side">
          <div className="UCS-toolbar">
            <div className="UCS-date-nav">
              <button className="UCS-nav-btn" onClick={() => changeWeek(-7)}>
                &#9664;
              </button>
              <span
                className="UCS-date-label"
                onClick={() => setIsCalendarVisible(!isCalendarVisible)}
              >
                {`${formatDate(weekStart)} - ${formatDate(weekEnd)}`}
              </span>
              <button className="UCS-nav-btn" onClick={() => changeWeek(7)}>
                &#9654;
              </button>

              {isCalendarVisible && (
                <div className="UCS-picker-dropdown">
                  <Calendar
                    onClickDay={onDateSelect}
                    value={currentWeekStart}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="UCS-note-banner">
            <p>
              View your upcoming care visits or book a new one. If your calendar
              is empty, no appointments have been scheduled yet.
            </p>
          </div>

          <div className="UCS-calendar-showcase">
            <div className="UCS-calendar-grid">
              <div className="UCS-time-col">
                <div className="UCS-time-placeholder"></div>
                {timeList.map((time, index) => (
                  <div key={index} className="UCS-time-slot">
                    <span>{time}</span>
                  </div>
                ))}
              </div>

              <div className="UCS-day-cols">
                {daysOfWeek.map((date, index) => (
                  <div key={index} className="UCS-day-col">
                    <div className="UCS-day-label">
                      <span className="UCS-weekday">
                        {date.toLocaleDateString("en-US", { weekday: "short" })}
                      </span>
                      <span className="UCS-daynum">
                        {date.toLocaleDateString("en-US", { day: "numeric" })}
                      </span>
                    </div>

                    {/* Lưới ngang hiển thị dưới background */}
                    <div className="UCS-day-bg">
                      {timeList.map((_, idx) => (
                        <div key={idx} className="UCS-bg-slot"></div>
                      ))}
                    </div>

                    {events
                      .filter(
                        (event) =>
                          stripTime(date).getTime() ===
                          stripTime(event.date_day).getTime(),
                      )
                      .map((event, eventIndex) => {
                        const style = getEventPosition(
                          event.start_time,
                          event.end_time,
                        );
                        let event_style;
                        let event_status;

                        switch (event.booking_acceptance) {
                          case true:
                            if (event.date_acceptance) {
                              event_style = "UCS-event-accepted";
                              event_status = "Accepted";
                            } else {
                              event_style = "UCS-event-denied";
                              event_status = "Denied";
                            }
                            break;
                          case null:
                            event_style = "UCS-event-pending";
                            event_status = "Pending";
                            break;
                          case false:
                            event_style = "UCS-event-denied";
                            event_status = "Denied";
                            break;
                          default:
                            event_style = "UCS-event-pending";
                            event_status = "Pending";
                            break;
                        }

                        return (
                          <div
                            key={eventIndex}
                            className={`UCS-event ${event_style} ${event.selected ? "UCS-event-selected" : ""}`}
                            onClick={() =>
                              selectEvent(
                                event,
                                eventIndex,
                                event_style,
                                event_status,
                              )
                            }
                            style={style}
                          >
                            <span className="UCS-event-provider">
                              {event.provider_name}
                            </span>
                            <span className="UCS-event-time">
                              {event.start_time} - {event.end_time}
                            </span>
                          </div>
                        );
                      })}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="UCS-details-side">
          <div className="UCS-details-header">
            <h2>Appointment Details</h2>
          </div>

          <div className="UCS-details-card">
            {eventDetail.length === 0 ? (
              <div className="UCS-details-empty">
                <img
                  src={require("../images/calendar_icon.png")}
                  alt=""
                  className="UCS-empty-icon"
                />
                <p className="UCS-details-text">
                  No appointments scheduled yet, or none selected.
                </p>
                <p className="UCS-details-text">
                  Click <strong>“Book a Care Visit”</strong> below to choose
                  a service, date, and time.
                </p>
              </div>
            ) : (
              <div className="UCS-details-content">
                <div className="UCS-detail-hero">
                  <div className="UCS-detail-hero-text">
                    <h1 className="UCS-detail-provider">
                      {eventDetail.provider_name}
                    </h1>
                    <p className="UCS-detail-service">
                      {eventDetail.care_service_name}
                    </p>
                  </div>
                  <span className={`UCS-status-badge ${eventStatusStyle}`}>
                    {eventStatus}
                  </span>
                </div>

                <div className="UCS-detail-meta">
                  <div className="UCS-meta-item">
                    <FiClock className="UCS-meta-icon" aria-hidden="true" />
                    <div>
                      <span className="UCS-meta-label">Time</span>
                      <span className="UCS-meta-value">
                        {eventDetail.start_time?.slice(0, 5)}
                        {eventDetail.end_time ? ` – ${eventDetail.end_time.slice(0, 5)}` : ""}
                      </span>
                    </div>
                  </div>
                  <div className="UCS-meta-item">
                    <FiCalendar className="UCS-meta-icon" aria-hidden="true" />
                    <div>
                      <span className="UCS-meta-label">Date</span>
                      <span className="UCS-meta-value">
                        {eventDetail.date_day || "—"}
                      </span>
                    </div>
                  </div>
                  <div className="UCS-meta-item">
                    <FiUser className="UCS-meta-icon" aria-hidden="true" />
                    <div>
                      <span className="UCS-meta-label">Client</span>
                      <span className="UCS-meta-value UCS-patient-name">
                        {eventDetail.patient_first_name}{" "}
                        {eventDetail.patient_last_name}
                      </span>
                    </div>
                  </div>
                  <div className="UCS-meta-item">
                    <FiBriefcase className="UCS-meta-icon" aria-hidden="true" />
                    <div>
                      <span className="UCS-meta-label">Service</span>
                      <span className="UCS-meta-value">
                        {eventDetail.care_service_name}
                      </span>
                    </div>
                  </div>
                </div>

                {(eventDetail.paid || eventDetail.active_reschedule_request_id) && (
                  <div className="UCS-detail-flags">
                    {eventDetail.paid && (
                      <span className="UCS-flag-chip paid">
                        <FiCheck aria-hidden="true" /> Paid
                      </span>
                    )}
                    {eventDetail.active_reschedule_request_id && (
                      <span className="UCS-flag-chip reschedule-pending">
                        <FiClock aria-hidden="true" /> Reschedule pending
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="UCS-actions-box">
            {eventDetail.length !== 0 && (
              <div
                className={`UCS-quick-actions cols-${
                  1 +
                  (needsPayment(eventDetail) ? 1 : 0) +
                  (!eventDetail.completed &&
                  !eventDetail.active_reschedule_request_id
                    ? 1
                    : 0)
                }`}
              >
                {!eventDetail.completed &&
                  !eventDetail.active_reschedule_request_id && (
                    <button
                      type="button"
                      className="UCS-quick-btn"
                      onClick={() => setRescheduleOpen(true)}
                    >
                      <FiRefreshCw aria-hidden="true" />
                      <span>Reschedule</span>
                    </button>
                  )}

                {needsPayment(eventDetail) && (
                  <button
                    type="button"
                    className="UCS-quick-btn UCS-quick-btn-pay"
                    onClick={requestPayment}
                    disabled={isPaying}
                    aria-busy={isPaying || undefined}
                  >
                    <FiCreditCard aria-hidden="true" />
                    <span>{isPaying ? "Processing…" : "Pay"}</span>
                  </button>
                )}

                <button
                  type="button"
                  className="UCS-quick-btn"
                  onClick={goToBookings}
                >
                  <FiEye aria-hidden="true" />
                  <span>View</span>
                </button>
              </div>
            )}

            <button
              onClick={openModal}
              className="UCS-action-btn primary"
              type="button"
            >
              <FiPlus className="UCS-btn-icon" aria-hidden="true" />
              Book a Care Visit
            </button>

            {eventDetail.length !== 0 && (
              <button
                type="button"
                className="UCS-cancel-link"
                onClick={togglePrompt}
              >
                Cancel this appointment
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default UserCalendarScreen;
